import React from 'react';
import { PlayIcon } from 'lucide-react';
import type { ArchiveItem } from '../../data/archive';

/** Deterministic bar heights, so a recording always looks like itself. */
function bars(seed: string, count = 44): number[] {
  let value = 0;
  for (let index = 0; index < seed.length; index += 1) {
    value = (value * 31 + seed.charCodeAt(index)) % 9973;
  }
  return Array.from({
    length: count
  }, (_, index) => {
    value = (value * 1103515245 + 12345) % 2147483648;
    const normalized = value / 2147483648 * 0.8 + 0.2;
    const envelope = Math.sin(index / count * Math.PI) * 0.5 + 0.5;
    return Math.round(normalized * envelope * 100);
  });
}
interface WaveformProps {
  seed: string;
  className?: string;
}
export function Waveform({
  seed,
  className = ''
}: WaveformProps) {
  return <span aria-hidden="true" className={`flex h-10 items-center gap-[2px] ${className}`} data-id="element-89" data-mp="2jexheyq5srwt">
      {bars(seed).map((height, index) => <span key={index} className="w-[3px] shrink-0 rounded-pill bg-current opacity-70" style={{
      height: `${Math.max(height, 8)}%`
    }} data-id="element-90" data-mp="2oftk58yry80n" data-mpi={index} />)}
    </span>;
}
interface AudioTileProps {
  item: ArchiveItem;
  onOpen: (item: ArchiveItem) => void;
}

/**
 * A recording is a voice, not a picture. The tile is a dark listening object
 * with a waveform and a line of transcript, so it is legible without playing.
 */
export function AudioTile({
  item,
  onOpen
}: AudioTileProps) {
  return <li className="break-inside-avoid" data-id="element-91" data-mp="o9r6u1fokdoj">
      <button type="button" onClick={() => onOpen(item)} className="w-full overflow-hidden rounded-card bg-ink p-5 text-left transition-opacity hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-92" data-mp="o8ol07x7t1fh">
        <span className="flex items-center justify-between gap-3" data-id="element-93" data-mp="1c0dfnuxie1zl">
          <span className="text-eyebrow-caption font-semibold uppercase text-on-ink opacity-70" data-id="element-94" data-mp="170hctz0jsglm">
            {item.year} · Recording
          </span>
          <span className="text-small text-on-ink opacity-70" data-id="element-95" data-mp="4oe379e7xdz8">{item.duration}</span>
        </span>

        <span className="mt-3 flex items-center gap-3 text-on-ink" data-id="element-96" data-mp="8oxwg1vo7xg3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-highlight" data-id="element-97" data-mp="dn96aclyu7yb">
            <PlayIcon aria-hidden="true" className="h-5 w-5 translate-x-0.5 text-on-highlight" data-id="element-98" data-mp="if9d8p0ge14e" />
          </span>
          <Waveform seed={item.id} className="min-w-0 flex-1 text-highlight" data-id="element-99" data-mp="ncj3g6vo2gta" />
        </span>

        <span className="mt-4 block text-body text-on-ink" data-id="element-100" data-mp="sb2mnzivlehy">{item.title}</span>

        {item.transcript ? <span className="mt-2 block text-small italic text-on-ink opacity-80" data-id="element-101" data-mp="weihqrfar6sj">
            {item.transcript}
          </span> : null}

        <span className="mt-3 block text-small text-on-ink opacity-60" data-id="element-102" data-mp="2y5lubm8qyvfq">
          {item.speaker ? `${item.speaker} · ` : ''}
          {item.credit}
        </span>
      </button>
    </li>;
}